import Head from "next/head";
import dynamic from "next/dynamic";

import { useDispatch, useSelector } from "react-redux";

import { GetServerSideProps, NextPage } from "next";
import Image from "next/legacy/image";

import Container from "@mui/material/Container";
import Box from "@mui/material/Box";

import useCart from "modules/Cart/useCart";

import { selectAuthModalOpen, setAuthModalOpen } from "redux/slices/metamask";

import ProductTile from "sections/storesPage/ProductTile";
import StoreDetails from "sections/storesPage/StoreDetails";
import useStoresProducts from "sections/storesPage/useStoresProducts";
import NewStoreDetails from "sections/storesPage/NewStoreDetails";

import GridItem from "components/Grid/GridItem";
import ListItems from "components/ListItems";
import Layout from "components/Layout";
import Header from "components/Header";
import Footer from "components/Footer";
import BackArrow from "components/BackArrow";
import AuthModal from "components/AuthModal";
import SocialGroup from "components/SocialGroup";

import { getByUrl } from "db/repository/ClientRepository";

import { STORE_VERSIONS } from "config/store";
import { APP_ROUTES } from "config/routes";

import { captureException } from "utils/errors";
import { getSubdomain } from "utils/store";
import { removeSpecialChars } from "utils/query";

import { Client } from "services/store/Store.interface";

const ConfirmationModal = dynamic(
  () => import("modules/Cart/ConfirmationModal"),
  { ssr: false },
);

interface StorePageProps {
  isEmbedded: boolean;
  data: Pick<
    Client,
    | "id"
    | "name"
    | "description"
    | "headerImage"
    | "version"
    | "background"
    | "backgroundColor"
    | "backgroundImage"
    | "startSellingDate"
    | "publishDate"
    | "isEmbeddedActive"
    | "isSubdomainActive"
    | "endDate"
    | "twitterUrl"
    | "facebookUrl"
    | "instagramUrl"
    | "youtubeUrl"
    | "websiteUrl"
    | "linkedinUrl"
    | "telegramUrl"
    | "discordUrl"
    | "tiktokUrl"
    | "openseaUrl"
    | "mediumUrl"
    | "isCreatedByForm"
    | "isLive"
  >;
}

const StorePage: NextPage<StorePageProps> = ({ data, isEmbedded }) => {
  const { isCartModalOpen, closeModal } = useCart();
  const { products, isProductsLoading, pagination, handleChangePage } =
    useStoresProducts(data?.id);

  const dispatch = useDispatch();
  const isAuthModalOpen = useSelector(selectAuthModalOpen);

  const closeAuthModal = () => {
    dispatch(setAuthModalOpen(false));
  };

  return (
    <>
      <Head>
        <title>{`${data?.name} | Uniqly Store Generator`}</title>
        <meta property="og:title" content={data.name} />
        <meta property="og:description" content={data.description} />
        <meta property="og:image" content={data.headerImage as string} />
      </Head>

      {(!data.version || data.version === STORE_VERSIONS["1"]) && (
        <Layout
          isEmbedded={isEmbedded}
          subHeader={{
            background:
              data.background ||
              `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/storesPage/default-background.png`,
          }}
        >
          <StoreDetails
            details={data}
            socialsComponent={<SocialGroup items={data} />}
            isEmbedded={isEmbedded}
          />
          <ListItems
            isEmbedded={isEmbedded}
            items={products}
            isLoading={isProductsLoading}
            pagination={pagination}
            handleChangePage={handleChangePage}
            renderItem={(item) => (
              <GridItem xs={12} lg={4} xl={3} key={`bundle-${item.id}`}>
                <ProductTile
                  isEmbedded={isEmbedded}
                  bundle={item}
                  showAddToCart
                />
              </GridItem>
            )}
          />
        </Layout>
      )}
      {data.version === STORE_VERSIONS["2"] && (
        <>
          <Header isEmbedded={isEmbedded} />
          <Box
            sx={{
              pt: { xs: 8, lg: 10 },
              backgroundColor: data.backgroundColor,
              ...(data.backgroundImage
                ? {
                    backgroundImage: `url(${data.backgroundImage})`,
                    backgroundRepeat: "repeat",
                  }
                : null),
            }}
          >
            <Container
              sx={{
                minHeight: isEmbedded ? "calc(100vh - 124px)" : "70vh",
                flex: 1,
                position: "relative",
                paddingBottom: { md: 10, lg: 8 },
              }}
            >
              {!isEmbedded && (
                <BackArrow
                  path={APP_ROUTES.STORES}
                  label="Back to stores list"
                  mb={0}
                />
              )}
              <Box
                display="flex"
                justifyContent={"center"}
                sx={{
                  mt: { xs: 2, md: 0 },
                  mx: "auto",
                  height: { xs: 90, sm: 160, md: 220, lg: 270 },
                  width: { xs: "100%", md: "80%" },
                }}
                position="relative"
              >
                {data.headerImage && (
                  <Image
                    src={data.headerImage}
                    layout="fill"
                    objectFit="contain"
                  />
                )}
              </Box>
              <Box
                justifyContent={"center"}
                width="100%"
                sx={{ bottom: "30px" }}
              >
                <NewStoreDetails
                  details={data}
                  socialsComponent={<SocialGroup items={data} />}
                />
              </Box>
              <ListItems
                isEmbedded={isEmbedded}
                items={products}
                isLoading={isProductsLoading}
                pagination={pagination}
                handleChangePage={handleChangePage}
                renderItem={(item) => (
                  <GridItem xs={12} lg={4} xl={3} key={`bundle-${item.id}`}>
                    <ProductTile
                      isEmbedded={isEmbedded}
                      bundle={item}
                      showAddToCart
                    />
                  </GridItem>
                )}
              />
            </Container>
          </Box>
          <Footer isEmbedded={isEmbedded} />
          {isCartModalOpen && (
            <ConfirmationModal isOpen={isCartModalOpen} onClose={closeModal} />
          )}
          {isAuthModalOpen && (
            <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
          )}
        </>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<StorePageProps> = async (
  ctx,
) => {
  const isEmbedded =
    !!getSubdomain(ctx.req.headers.host as string) || !!ctx.query?.embedded;

  try {
    const client = await getByUrl(removeSpecialChars(ctx.query.slug as string));
    const data = JSON.parse(JSON.stringify(client));
    if (data.isCreatedByForm && !data.isLive) {
      throw new Error("Store is not public yet!");
    }

    return {
      props: {
        data: {
          ...data,
          publishDate:
            client?.publishDate && new Date(client.publishDate).getTime(),
          startSellingDate:
            client?.startSellingDate &&
            new Date(client.startSellingDate).getTime(),
        },
        isEmbedded,
      },
    };
  } catch (err) {
    captureException(err);
    return {
      redirect: {
        destination: "/404",
        permanent: false,
      },
    };
  }
};

export default StorePage;
